import { useAuth } from "react-oidc-context";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Box,
  Chip,
  Breadcrumbs,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import { useState } from "react";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { SIDEBAR_WIDTH } from "./sidebarConfig";
import { useBreadcrumbs, type BreadcrumbItem } from "./useBreadcrumbs";
import { useAppSettings } from "../contexts/AppSettingsContext";
import { useBreadcrumbDetail } from "../contexts/BreadcrumbContext";

export default function TopBar({
  title,
  menuButton,
  breadcrumbs: breadcrumbsOverride,
}: {
  title: string;
  menuButton?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}) {
  const auth = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { detailLabel } = useBreadcrumbDetail();
  const defaultCrumbs = useBreadcrumbs(detailLabel);
  const breadcrumbs = breadcrumbsOverride ?? defaultCrumbs;
  const { environmentLabel } = useAppSettings();

  const userName =
    auth.user?.profile?.preferred_username ??
    auth.user?.profile?.name ??
    "User";
  const initials = userName.slice(0, 2).toUpperCase();

  const topBarTheme = useTheme();
  const isMobileTopBar = useMediaQuery(topBarTheme.breakpoints.down("md"));

  const showBreadcrumbs = breadcrumbs.length > 1;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: isMobileTopBar ? "100%" : `calc(100% - ${SIDEBAR_WIDTH}px)`,
        ml: isMobileTopBar ? 0 : `${SIDEBAR_WIDTH}px`,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        boxShadow: "var(--flux-shadow-sm)",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
          {menuButton}
          <Box sx={{ minWidth: 0 }}>
            {showBreadcrumbs ? (
              <Breadcrumbs
                separator={<NavigateNextRoundedIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 0.25 }}
              >
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  if (isLast || !crumb.path) {
                    return (
                      <Typography
                        key={`${crumb.label}-${index}`}
                        variant="body2"
                        color={isLast ? "text.primary" : "text.secondary"}
                        fontWeight={isLast ? 600 : 400}
                        noWrap
                      >
                        {crumb.label}
                      </Typography>
                    );
                  }
                  return (
                    <Link
                      key={`${crumb.label}-${index}`}
                      component={RouterLink}
                      to={crumb.path}
                      underline="hover"
                      color="text.secondary"
                      variant="body2"
                      noWrap
                    >
                      {crumb.label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            ) : null}
            <Typography variant="h6" noWrap>
              {title}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
          <Chip label={environmentLabel} size="small" color="secondary" variant="outlined" />
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small" aria-label="Account menu">
            <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.main", fontSize: "0.8rem", fontWeight: 700 }}>
              {initials}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1">{userName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {auth.user?.profile?.email ?? ""}
            </Typography>
          </Box>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon><PersonRoundedIcon fontSize="small" /></ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={() => auth.signoutRedirect()}>
            <ListItemIcon><LogoutRoundedIcon fontSize="small" /></ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
